
import { Injectable, inject } from '@angular/core';
import { TranslationService } from './translation.service';

export interface FriendlyError {
  title: string;
  message: string;
  suggestion?: string;
  actionLabel?: string;
  actionLink?: string;
  isTechnical?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ErrorMappingService {
  private translationService = inject(TranslationService);

  mapError(error: any): FriendlyError {
    // Extract raw message safely
    let rawMessage = '';
    if (typeof error === 'string') {
        rawMessage = error;
    } else if (error && typeof error === 'object') {
        rawMessage = error.message || error.error_description || JSON.stringify(error);
    }
    
    rawMessage = rawMessage.toLowerCase();
    const code = error?.code || '';

    // 1. Insufficient Funds (Wallet)
    if (rawMessage.includes('insufficient_funds') || rawMessage.includes('ยอดเงินไม่เพียงพอ')) {
      return {
        title: this.t('errors.insufficientFunds.title'),
        message: this.t('errors.insufficientFunds.message'),
        suggestion: this.t('errors.insufficientFunds.suggestion'),
        actionLabel: 'ไปหน้าเติมเงิน',
        actionLink: '/admin/billing'
      };
    }

    // 2. Quota Exceeded (Plan Limits)
    if (rawMessage.includes('quota') || rawMessage.includes('limit exceeded') || rawMessage.includes('โควต้า')) {
       return {
        title: this.t('errors.quotaExceeded.title'),
        message: this.t('errors.quotaExceeded.message'),
        suggestion: this.t('errors.quotaExceeded.suggestion'),
        actionLabel: 'อัปเกรดแผน',
        actionLink: '/admin/billing'
      };
    }

    // 3. Duplicates (Unique Constraint)
    if (code === '23505' || rawMessage.includes('unique constraint') || rawMessage.includes('duplicate') || rawMessage.includes('already exists')) {
       return {
        title: this.t('errors.duplicateEntry.title'),
        message: this.t('errors.duplicateEntry.message'),
        suggestion: this.t('errors.duplicateEntry.suggestion')
      };
    }

    // 4. Not Found (PGRST116 or 404)
    if (code === 'PGRST116' || rawMessage.includes('not found') || rawMessage.includes('404')) {
       return {
        title: this.t('errors.notFound.title'),
        message: this.t('errors.notFound.message'),
        suggestion: this.t('errors.notFound.suggestion')
      };
    }
    
    // 5. Permission Denied (RLS / 403 / 401)
    if (code === '42501' || rawMessage.includes('permission denied') || rawMessage.includes('access denied')) {
        return {
            title: this.t('errors.permissionDenied.title'),
            message: this.t('errors.permissionDenied.message'),
            suggestion: this.t('errors.permissionDenied.suggestion')
        };
    }

    // 6. Auth Errors
    if (rawMessage.includes('invalid login') || rawMessage.includes('invalid_grant')) {
       return {
        title: 'เข้าสู่ระบบไม่สำเร็จ',
        message: this.t('errors.auth.invalidLogin'),
        suggestion: 'กรุณาตรวจสอบอีเมลและรหัสผ่าน'
      };
    }
    
    if (rawMessage.includes('user not found')) {
        return {
            title: 'ไม่พบบัญชีผู้ใช้',
            message: this.t('errors.auth.userNotFound'),
            suggestion: 'กรุณาตรวจสอบอีเมลอีกครั้ง'
        };
    }

    // Default Generic - But keep original message if it seems readable
    // If message is too long or json, default to generic
    const displayMessage = rawMessage.length > 100 || rawMessage.includes('{') ? this.t('errors.generic') : error.message || this.t('errors.generic');

    return {
      title: 'ข้อผิดพลาด (Error)',
      message: displayMessage,
      suggestion: '',
      isTechnical: true // Flag to show "Copy Error" button in UI
    };
  }

  private t(key: string): string {
    return this.translationService.translate(key);
  }
}
